import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderPlus, Loader2, TriangleAlert } from 'lucide-react';

import { assistantsService, type Projet } from '../../services/api/assistants';

interface Props {
  conversationId: number;
  projetActuel: number | null;
  /** Assistant de l'échange : le nouvel échange s'ouvre sur le même. */
  templateId?: number;
  /** Un échange déjà entamé ne peut pas changer de dossier sans conséquence. */
  aDesMessages?: boolean;
  onChange?: (projet: number | null, nom: string | null) => void;
}

/**
 * Rattachement d'une conversation à une activité.
 *
 * Le rattachement est facultatif et se fait après coup : on ouvre l'assistant,
 * on travaille, on range.
 *
 * Changer de dossier en cours d'échange est une autre affaire. Les documents
 * du dossier sont lus par l'assistant : en changer reviendrait à poursuivre une
 * conversation dont les réponses précédentes reposaient sur d'autres pièces.
 * On ouvre donc un échange neuf, après avoir demandé ce qu'il advient du
 * précédent.
 */
const SelecteurProjet: React.FC<Props> = ({
  conversationId,
  projetActuel,
  templateId,
  aDesMessages,
  onChange,
}) => {
  const navigate = useNavigate();
  const [projets, setProjets] = useState<Projet[]>([]);
  const [selection, setSelection] = useState<number | null>(projetActuel);
  const [creation, setCreation] = useState(false);
  const [nouveauNom, setNouveauNom] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');
  // Dossier vers lequel on s'apprête à basculer, en attente de confirmation.
  const [enAttente, setEnAttente] = useState<{ id: number | null; nom: string } | null>(null);

  useEffect(() => {
    assistantsService.listerProjets().then(setProjets).catch(() => setProjets([]));
  }, []);

  useEffect(() => setSelection(projetActuel), [projetActuel]);

  /** Rattachement simple, quand l'échange n'a encore rien produit. */
  const rattacher = async (id: number | null) => {
    setOccupe(true);
    setErreur('');
    try {
      const reponse = await assistantsService.rattacherConversation(conversationId, id);
      setSelection(reponse.project);
      onChange?.(reponse.project, reponse.project_name);
    } catch (e: any) {
      setErreur(e.message || 'Rattachement impossible.');
    } finally {
      setOccupe(false);
    }
  };

  /** Ouvre un échange neuf dans le dossier visé, et règle le sort de l'ancien. */
  const basculer = async (supprimerAncien: boolean) => {
    if (!enAttente || !templateId) return;
    setOccupe(true);
    setErreur('');
    try {
      const nouvelle = await assistantsService.ouvrirConversation(
        templateId,
        enAttente.id ?? undefined,
      );
      if (supprimerAncien) {
        await assistantsService.supprimerConversation(conversationId);
      }
      navigate(`/workspace/conversation/${nouvelle.id}`);
    } catch (e: any) {
      setErreur(e.message || "L'échange n'a pas pu être ouvert.");
      setOccupe(false);
      setEnAttente(null);
    }
  };

  const choisir = (valeur: string) => {
    const id = valeur ? Number(valeur) : null;
    if (id === selection) return;

    // Échange vierge, ou assistant inconnu : rien à préserver, on rattache.
    if (!aDesMessages || !templateId) {
      rattacher(id);
      return;
    }
    setEnAttente({
      id,
      nom: id ? projets.find((p) => p.id === id)?.name || 'ce dossier' : 'aucun dossier',
    });
  };

  const creer = async () => {
    const nom = nouveauNom.trim();
    if (!nom) return;
    setOccupe(true);
    setErreur('');
    try {
      const projet = await assistantsService.creerProjet(nom);
      setProjets((p) => [projet, ...p]);
      setNouveauNom('');
      setCreation(false);
      setOccupe(false);
      choisir(String(projet.id));
    } catch (e: any) {
      setErreur(e.message || 'Création impossible.');
      setOccupe(false);
    }
  };

  if (creation) {
    return (
      <div className="flex items-center gap-2">
        <input
          autoFocus
          value={nouveauNom}
          onChange={(e) => setNouveauNom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') creer();
            if (e.key === 'Escape') setCreation(false);
          }}
          placeholder="Ex. : Atelier MLM Kankan — juin 2026"
          className="w-72 rounded-md border border-[#DDE2DD] px-3 py-1.5 text-sm
            focus:border-eg-accent focus:outline-none"
        />
        <button type="button" onClick={creer} disabled={occupe || !nouveauNom.trim()}
          className="rounded-md bg-eg-ombre px-3 py-1.5 text-sm text-eg-blanc disabled:opacity-50">
          Créer
        </button>
        <button type="button" onClick={() => setCreation(false)}
          className="text-sm text-eg-gris hover:text-eg-brun">
          Annuler
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-eg-gris">Activité</span>
        <select
          value={selection ?? ''}
          disabled={occupe}
          onChange={(e) => choisir(e.target.value)}
          className="rounded-md border border-[#DDE2DD] bg-eg-blanc px-2 py-1.5
            focus:border-eg-accent focus:outline-none disabled:opacity-50"
        >
          <option value="">Aucune</option>
          {projets.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setCreation(true)}
          className="inline-flex items-center gap-1 text-eg-accent hover:text-eg-encre"
          title="Créer une activité"
        >
          <FolderPlus className="w-4 h-4" />
        </button>
        {occupe && <Loader2 className="w-4 h-4 animate-spin text-eg-estompe" />}
        {erreur && <span className="text-eg-accent">{erreur}</span>}
      </div>

      {enAttente && (
        <div className="absolute right-0 top-full z-30 mt-2 w-[27rem] rounded-md border
          border-[#C9D0C9] bg-[#EEF2ED] p-4 text-left">
          <div className="flex items-start gap-2.5">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-eg-alerte" />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-eg-encre">
                Un nouvel échange va s'ouvrir dans « {enAttente.nom} »
              </p>
              <p className="mt-1 text-[12.5px] leading-relaxed text-eg-gris">
                L'assistant lira les documents de ce dossier. Poursuivre l'échange en
                cours serait trompeur : les réponses déjà obtenues s'appuyaient sur
                d'autres pièces.
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => basculer(false)}
              disabled={occupe}
              className="rounded-[5px] bg-eg-ombre px-3 py-1.5 text-[12.5px] font-semibold
                text-eg-blanc transition-colors hover:bg-eg-encre disabled:opacity-50"
            >
              Conserver l'échange en cours
            </button>
            <button
              type="button"
              onClick={() => basculer(true)}
              disabled={occupe}
              className="rounded-[5px] border border-eg-accent/40 bg-eg-blanc px-3 py-1.5
                text-[12.5px] font-semibold text-eg-accent transition-colors
                hover:bg-eg-accent/10 disabled:opacity-50"
            >
              Le supprimer
            </button>
            <button
              type="button"
              onClick={() => setEnAttente(null)}
              disabled={occupe}
              className="px-2 py-1.5 text-[12.5px] text-eg-gris hover:text-eg-brun
                disabled:opacity-50"
            >
              Annuler
            </button>
            {occupe && <Loader2 className="h-4 w-4 animate-spin text-eg-estompe" />}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelecteurProjet;
